import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { RequirePermission } from "@/components/RequirePermission";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { adminUsersApi } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { useRoles } from "@/lib/roles";
import type { AdminUserCreate, OperatorRole } from "@/lib/types";

export default function UsersList() {
  const { t } = useTranslation();
  const [role, setRole] = useState<OperatorRole | "">("");
  const roles = useRoles();
  const [active, setActive] = useState<"all" | "yes" | "no">("all");
  const [q, setQ] = useState("");
  const [openCreate, setOpenCreate] = useState(false);

  const query = useQuery({
    queryKey: ["admin", "users", { role, active, q }],
    queryFn: () =>
      adminUsersApi.list({
        role: role || undefined,
        is_active: active === "all" ? undefined : active === "yes",
        q: q.trim() || undefined,
        page_size: 100,
      }),
  });

  return (
    <RequirePermission codes="user.manage">
      <PageHeader
        eyebrow="admin · users"
        title={t("users.title")}
        description={t("users.description")}
        actions={
          <button className="btn btn-primary" onClick={() => setOpenCreate(true)}>
            {t("users.newUser")}
          </button>
        }
      />

      <div className="card p-4 flex flex-wrap gap-4 items-end">
        <label className="block text-sm">
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">{t("users.role")}</span>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="mt-1 px-3 py-2 rounded-md bg-surface border border-border font-mono text-xs"
          >
            <option value="">{t("users.allFilter")}</option>
            {roles.all.map((r) => (
              <option key={r.role} value={r.role}>{r.name}</option>
            ))}
          </select>
        </label>
        <label className="block text-sm">
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">{t("users.activeFilter")}</span>
          <select
            value={active}
            onChange={(e) => setActive(e.target.value as "all" | "yes" | "no")}
            className="mt-1 px-3 py-2 rounded-md bg-surface border border-border font-mono text-xs"
          >
            <option value="all">{t("users.allFilter")}</option>
            <option value="yes">{t("users.onlyActive")}</option>
            <option value="no">{t("users.onlyDisabled")}</option>
          </select>
        </label>
        <label className="block text-sm flex-1 min-w-[12rem]">
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">
            {t("users.searchLabel")}
          </span>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="mt-1 w-full px-3 py-2 rounded-md bg-surface border border-border"
            placeholder={t("users.searchPlaceholder")}
          />
        </label>
      </div>

      {query.isLoading ? (
        <LoadingState />
      ) : query.isError ? (
        <ErrorState error={query.error} />
      ) : query.data!.items.length === 0 ? (
        <EmptyState title={t("users.nothingFound")} hint={t("users.nothingFoundHint")} />
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-alt">
              <tr className="text-left text-text-muted font-mono text-xs uppercase">
                <th className="px-4 py-3">{t("users.colName")}</th>
                <th className="px-4 py-3">Login</th>
                <th className="px-4 py-3">{t("users.colRole")}</th>
                <th className="px-4 py-3">{t("users.colStatus")}</th>
                <th className="px-4 py-3">{t("users.colLastLogin")}</th>
              </tr>
            </thead>
            <tbody>
              {query.data!.items.map((u) => (
                <tr key={u.id} className="border-t border-border hover:bg-surface-hover">
                  <td className="px-4 py-3">
                    <Link to={`/admin/users/${u.id}`} className="text-accent-strong hover:underline">
                      {u.name}
                    </Link>
                    {u.must_change_password && (
                      <span className="ml-2 pill text-xs" title={t("users.mustChangeTitle")}>
                        {t("users.mustChange")}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-text-secondary">
                    {u.login ?? "—"}
                  </td>
                  <td className="px-4 py-3">
                    <span className="pill pill-accent">{roles.label(u.role)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`pill ${u.is_active ? "pill-ok" : ""}`}>
                      {u.is_active ? t("users.enabled") : t("users.disabled")}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-text-muted font-mono text-xs">
                    {u.last_login_at ? formatDateTime(u.last_login_at) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openCreate && (
        <CreateUserDialog onClose={() => setOpenCreate(false)} />
      )}
    </RequirePermission>
  );
}

function CreateUserDialog({ onClose }: { onClose: () => void }) {
  const roles = useRoles();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [data, setData] = useState<AdminUserCreate>({
    name: "",
    role: "OPERATOR",
    login: null,
    password: null,
    nfc_tag: null,
    pin: null,
    is_active: true,
    must_change_password: true,
  });
  const [err, setErr] = useState<string | null>(null);

  const m = useMutation({
    mutationFn: () => adminUsersApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
      onClose();
    },
    onError: (e) => setErr(e instanceof Error ? e.message : t("common.error")),
  });

  function patch<K extends keyof AdminUserCreate>(k: K, v: AdminUserCreate[K]) {
    setData((d) => ({ ...d, [k]: v }));
  }

  return (
    <div className="fixed inset-0 bg-page/70 backdrop-blur z-20 flex items-center justify-center p-6">
      <form
        onSubmit={(e) => { e.preventDefault(); setErr(null); m.mutate(); }}
        className="card p-6 w-full max-w-md space-y-4 max-h-[90vh] overflow-y-auto"
      >
        <header className="flex items-center justify-between gap-4">
          <h2 className="text-xl">{t("users.modalTitle")}</h2>
          <button type="button" className="btn text-xs" onClick={onClose}>×</button>
        </header>

        <Field label={t("users.fullName")} required>
          <input className="w-full px-3 py-2 rounded-md bg-surface border border-border" value={data.name} onChange={(e) => patch("name", e.target.value)} />
        </Field>
        <Field label={t("users.role")}>
          <select
            className="w-full px-3 py-2 rounded-md bg-surface border border-border"
            value={data.role}
            onChange={(e) => patch("role", e.target.value)}
          >
            {roles.assignable.map((r) => (
              <option key={r.role} value={r.role}>{r.name}</option>
            ))}
          </select>
        </Field>

        <fieldset className="border border-border rounded-md p-3 space-y-3">
          <legend className="text-xs font-mono uppercase tracking-wider text-text-muted px-2">
            {t("users.loginPasswordLegend")}
          </legend>
          <Field label={t("users.login")}>
            <input
              className="w-full px-3 py-2 rounded-md bg-surface border border-border font-mono"
              value={data.login ?? ""}
              onChange={(e) => patch("login", e.target.value || null)}
            />
          </Field>
          <Field label={t("users.passwordLabel")}>
            <input
              className="w-full px-3 py-2 rounded-md bg-surface border border-border"
              type="password"
              value={data.password ?? ""}
              onChange={(e) => patch("password", e.target.value || null)}
            />
          </Field>
        </fieldset>

        <fieldset className="border border-border rounded-md p-3 space-y-3">
          <legend className="text-xs font-mono uppercase tracking-wider text-text-muted px-2">
            {t("users.nfcPinLegend")}
          </legend>
          <Field label="NFC tag">
            <input
              className="w-full px-3 py-2 rounded-md bg-surface border border-border font-mono"
              value={data.nfc_tag ?? ""}
              onChange={(e) => patch("nfc_tag", e.target.value || null)}
            />
          </Field>
          <Field label={t("users.pinLabel")}>
            <input
              className="w-full px-3 py-2 rounded-md bg-surface border border-border font-mono"
              value={data.pin ?? ""}
              inputMode="numeric"
              onChange={(e) => patch("pin", e.target.value || null)}
            />
          </Field>
        </fieldset>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={data.must_change_password ?? true}
            onChange={(e) => patch("must_change_password", e.target.checked)}
          />
          {t("users.forceChangePassword")}
        </label>

        {err && <ErrorBanner onDismiss={() => setErr(null)}>{err}</ErrorBanner>}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
          <button type="button" className="btn" onClick={onClose} disabled={m.isPending}>
            {t("common.cancel")}
          </button>
          <button type="submit" className="btn btn-primary" disabled={m.isPending}>
            {m.isPending ? t("common.creating") : t("common.create")}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-xs font-mono uppercase tracking-wider text-text-muted">
        {label}{required && <span className="text-danger ml-1">*</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
